import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Palette,
  Eye,
  TrendingUp,
  MapPin,
  Award,
  Target,
  Phone,
  MessageCircle,
} from "lucide-react";
import QuoteDialog from "@/components/site/QuoteDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TITLE = "Création de logo à l'île Maurice — EM Group | Agence premium";
const DESCRIPTION =
  "Création de logo à l'île Maurice par EM Group : identité visuelle sur-mesure, branding complet, accompagnement stratégique pour entreprises mauriciennes ambitieuses. Devis sous 24h.";
const URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/creation-logo-ile-maurice`
    : "";

const FAQ_ITEMS = [
  {
    q: "Combien coûte une création de logo à l'île Maurice ?",
    a: "Chez EM Group, la création de logo à l'île Maurice démarre à 699 € TTC avec le Pack Logo Essentiel (logo, déclinaisons, fichiers HD et 2 visuels offerts). Pour un branding complet (palette, typographies, charte graphique, univers visuel), comptez à partir de 1599 € TTC. Le tarif final dépend de votre secteur et de l'ampleur du projet. Un devis détaillé vous est systématiquement adressé avant toute commande — sans engagement.",
  },
  {
    q: "Pourquoi faire appel à une agence pour créer son logo à Maurice ?",
    a: "Le marché mauricien est dynamique mais exigeant : tourisme, hôtellerie, services financiers, retail, tech… chaque secteur fait face à une concurrence internationale. Un logo amateur dessert immédiatement votre crédibilité. EM Group conçoit des logos pensés pour le niveau d'exigence attendu par une clientèle locale et internationale : esthétique premium, fichiers vectoriels professionnels, et accompagnement stratégique.",
  },
  {
    q: "Combien de temps pour créer un logo professionnel à Maurice ?",
    a: "Comptez 5 à 10 jours pour un Pack Logo Essentiel et 2 à 3 semaines pour un Pack Branding Complet. Ce délai inclut un brief approfondi, plusieurs pistes créatives, les révisions et la livraison des fichiers finaux. Une production accélérée est possible en cas d'ouverture imminente (hôtel, restaurant, boutique) — précisez-le dès le brief.",
  },
  {
    q: "Travaillez-vous uniquement à Maurice ou aussi à distance ?",
    a: "Nous accompagnons des clients partout à l'île Maurice — Port-Louis, Grand Baie, Flic-en-Flac, Ébène, Quatre Bornes, Curepipe — en visio, téléphone, WhatsApp ou rendez-vous physique selon vos préférences. Nous intervenons également à La Réunion, à Paris, partout en France et à l'international. Le processus reste identique : brief stratégique, propositions, révisions et livraison.",
  },
  {
    q: "Est-il possible de faire évoluer son logo plus tard ?",
    a: "Oui, bien sûr. Les fichiers sources vectoriels vous sont toujours remis, ce qui vous permet de faire évoluer votre logo à tout moment : refresh, déclinaisons, nouvelles versions. Vous pouvez également passer du Pack Logo au Pack Branding Complet à tout moment, ou basculer sur un accompagnement 360° lorsque votre entreprise grandit.",
  },
];

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function CreationLogoIleMaurice() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    setMeta("description", DESCRIPTION);
    setMeta(
      "keywords",
      "création de logo île Maurice, logo Maurice, branding Maurice, identité visuelle Maurice, agence digitale Maurice, graphiste Maurice, création logo entreprise Maurice, logo Port-Louis"
    );
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESCRIPTION, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "fr_MU", "property");
    if (URL) setMeta("og:url", URL, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", TITLE);
    setMeta("twitter:description", DESCRIPTION);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (URL) canonical.setAttribute("href", URL);

    // JSON-LD Service
    let ld = document.getElementById("ld-logo-maurice");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "ld-logo-maurice";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Création de logo et identité visuelle",
      provider: {
        "@type": "LocalBusiness",
        name: "EM Group",
        email: "contact@emgroupdigital.com",
        telephone: "+23057438178",
        url: URL || "https://emgroupdigital.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Beau Bassin – Rose Hill",
          addressCountry: "MU",
        },
        areaServed: [
          { "@type": "Country", name: "Maurice" },
          { "@type": "Place", name: "La Réunion" },
          { "@type": "Place", name: "France" },
        ],
      },
      areaServed: { "@type": "Country", name: "Maurice" },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        valueAddedTaxIncluded: true,
        price: "699",
        description:
          "Pack Logo Essentiel : logo + déclinaisons + 2 visuels offerts.",
      },
      description: DESCRIPTION,
    });

    // JSON-LD FAQ
    let ldFaq = document.getElementById("ld-logo-maurice-faq");
    if (!ldFaq) {
      ldFaq = document.createElement("script");
      ldFaq.type = "application/ld+json";
      ldFaq.id = "ld-logo-maurice-faq";
      document.head.appendChild(ldFaq);
    }
    ldFaq.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    return () => {
      document.title = prevTitle;
    };
  }, []);

  const openQuote = () => setQuoteOpen(true);

  return (
    <div
      className="min-h-screen bg-[#050507] text-white"
      data-testid="page-logo-ile-maurice"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home-logo">
            <img
              src="/brand/logo-em-group.png"
              alt="EM Group"
              className="h-10 w-10 object-contain"
            />
            <span className="font-outfit tracking-tight text-lg">
              EM <span className="text-white/50">Group</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft size={16} /> Accueil
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(229,36,49,0.2),transparent_55%),radial-gradient(ellipse_at_70%_70%,rgba(31,44,110,0.25),transparent_55%)]" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
            <MapPin size={14} className="text-[#E52431]" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/75">
              Île Maurice · Océan Indien
            </span>
          </div>

          <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05]">
            Création de logo{" "}
            <span className="gradient-text font-medium">à l&rsquo;île Maurice</span>
            <br className="hidden md:block" />
            <span className="text-white/85">pour entreprises ambitieuses</span>
          </h1>

          <p className="mt-8 text-white/75 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
            <strong className="text-white font-normal">
              À Maurice, votre image parle avant vous.
            </strong>{" "}
            Dans un marché où tourisme, hôtellerie, services et tech
            s&rsquo;adressent à une clientèle locale et internationale, un
            logo amateur casse la crédibilité — un logo professionnel la
            construit.
          </p>

          <p className="mt-5 text-white/65 text-base md:text-lg font-light leading-relaxed max-w-3xl">
            EM Group conçoit des logos sur-mesure pour les entrepreneurs,
            hôteliers, restaurateurs, PME et start-ups basés à{" "}
            <strong className="text-white font-normal">
              l&rsquo;île Maurice
            </strong>
            , pensés pour incarner votre positionnement et{" "}
            <strong className="text-white font-normal">
              convertir vos prospects en clients
            </strong>
            .
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={openQuote}
              data-testid="hero-cta-logo-maurice"
              className="inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
            >
              Créer mon logo dès maintenant
              <ArrowRight size={16} />
            </button>
            <a
              href="#packs"
              data-testid="hero-secondary-packs"
              className="inline-flex items-center gap-2 rounded-full glass text-white font-medium px-8 py-5 text-sm uppercase tracking-[0.15em] hover:bg-white/10 transition-all duration-300"
            >
              Voir les tarifs
            </a>
          </div>
          <p className="mt-5 text-xs text-white/50 font-light">
            ⚡ Réponse sous 24h · Devis personnalisé · Sans engagement
          </p>
        </div>
      </section>

      {/* Service presentation */}
      <section className="py-20 md:py-28 section-light">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Le service
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Un logo à la hauteur de vos ambitions mauriciennes
          </h2>
          <div className="mt-8 space-y-5 text-[#0A0A0A]/70 text-lg font-light leading-relaxed">
            <p>
              Un logo n&rsquo;est pas un visuel décoratif. C&rsquo;est la{" "}
              <span className="text-[#0A0A0A] font-normal">
                première promesse de votre marque
              </span>{" "}
              — ce que vos clients, partenaires et investisseurs perçoivent
              avant même d&rsquo;échanger avec vous.
            </p>
            <p>
              Chez EM Group, chaque{" "}
              <strong className="text-[#0A0A0A] font-normal">
                création de logo à l&rsquo;île Maurice
              </strong>{" "}
              commence par une analyse stratégique : votre secteur, votre
              clientèle (locale, touristique, internationale), vos valeurs
              et votre positionnement. Nous produisons ensuite un logo
              cohérent, impactant et durable, capable de porter votre marque
              à l&rsquo;échelle d&rsquo;une région comme à l&rsquo;étranger.
            </p>
            <p>
              Notre approche ne s&rsquo;arrête pas au design. Nous vous
              accompagnons de l&rsquo;identité visuelle jusqu&rsquo;à vos
              premiers clients, avec une vision 360° et une exécution
              pensée pour la performance commerciale.
            </p>
            <p>
              <a
                href="#accompagnement"
                data-testid="service-link-accompagnement"
                className="inline-flex items-center gap-2 text-[#E52431] font-medium hover:underline"
              >
                Découvrir notre accompagnement complet →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Branding importance */}
      <section className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
                — Branding & croissance
              </span>
              <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
                À Maurice, le branding fait la{" "}
                <span className="gradient-text font-medium">différence</span>.
              </h2>
              <div className="mt-8 space-y-5 text-white/70 text-lg font-light leading-relaxed">
                <p>
                  L&rsquo;île Maurice attire chaque année{" "}
                  <strong className="text-white font-normal">
                    plus d&rsquo;un million de visiteurs
                  </strong>{" "}
                  et des milliers d&rsquo;entreprises internationales. Dans
                  ce contexte, vos clients comparent votre marque à des
                  références mondiales, pas seulement à la concurrence
                  locale.
                </p>
                <p>
                  Selon plusieurs études, plus de{" "}
                  <strong className="text-white font-normal">
                    60% des décisions d&rsquo;achat
                  </strong>{" "}
                  sont influencées par la perception visuelle d&rsquo;une
                  marque. Un logo professionnel, combiné à une identité
                  cohérente (couleurs, typographies, ton de marque),
                  rassure, crédibilise et{" "}
                  <strong className="text-white font-normal">
                    déclenche l&rsquo;acte d&rsquo;achat
                  </strong>
                  .
                </p>
                <p>
                  Sur une île où tout se joue sur la recommandation, le
                  visuel et la réputation en ligne, un branding
                  professionnel n&rsquo;est plus un confort —
                  c&rsquo;est une condition de croissance.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-gradient-to-br from-[#1a0f12] to-[#0B1030] border border-white/10 p-8 space-y-5">
                {[
                  { icon: Eye, label: "Reconnaissance immédiate" },
                  { icon: Award, label: "Crédibilité internationale" },
                  { icon: TrendingUp, label: "Hausse du taux de conversion" },
                  { icon: Target, label: "Différenciation concurrentielle" },
                ].map((it) => {
                  const Icon = it.icon;
                  return (
                    <div key={it.label} className="flex items-center gap-4">
                      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#E52431]/20 to-[#1F2C6E]/30 border border-white/10 grid place-items-center flex-shrink-0">
                        <Icon size={18} className="text-white" strokeWidth={1.5} />
                      </div>
                      <span className="text-white font-light">{it.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packs */}
      <section id="packs" className="py-20 md:py-28 section-light">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Nos packs logo & branding
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Une création de logo{" "}
            <span className="gradient-text font-medium">pour chaque ambition</span>
          </h2>
          <p className="mt-6 text-[#0A0A0A]/60 text-lg font-light max-w-2xl">
            Du logo seul au branding complet, nous proposons des offres
            claires et transparentes pour les entreprises mauriciennes
            — locales comme internationales.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                name: "Pack Logo Essentiel",
                price: "699 € TTC",
                items: [
                  "Logo sur-mesure",
                  "Déclinaisons réseaux",
                  "Adaptation couleurs",
                  "Fichiers HD & sources",
                  "2 visuels offerts",
                ],
              },
              {
                name: "Pack Branding Complet",
                price: "À partir de 1599 € TTC",
                highlight: true,
                items: [
                  "Logo + déclinaisons",
                  "Palette couleurs",
                  "Typographies",
                  "Charte graphique",
                  "Univers visuel",
                  "Templates",
                ],
              },
              {
                name: "Pack Visibilité",
                price: "À partir de 2199 € TTC",
                items: [
                  "Branding complet",
                  "4 visuels réseaux",
                  "Mise en place réseaux",
                  "Accompagnement lancement",
                ],
              },
            ].map((p) => (
              <div
                key={p.name}
                data-testid={`pack-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`relative rounded-3xl p-8 transition-all duration-500 ${
                  p.highlight
                    ? "bg-gradient-to-b from-[#0A0A0A] to-[#141418] text-white border border-[#E52431]/40 glow-red"
                    : "light-card"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                      Le plus choisi
                    </div>
                  </div>
                )}
                <h3
                  className={`font-outfit text-xl font-medium ${
                    p.highlight ? "text-white" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.name}
                </h3>
                <div
                  className={`mt-4 font-outfit text-4xl font-light ${
                    p.highlight ? "gradient-text" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.price}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.items.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-3 text-sm ${
                        p.highlight ? "text-white/80" : "text-[#0A0A0A]/70"
                      }`}
                    >
                      <Check
                        size={15}
                        className="text-[#E52431] mt-0.5 flex-shrink-0"
                      />
                      <span className="font-light">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={openQuote}
                  data-testid={`pack-cta-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`mt-7 w-full rounded-full py-3 font-semibold text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                    p.highlight
                      ? "gradient-bg text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.5)]"
                      : "bg-[#0A0A0A] text-white hover:bg-[#E52431]"
                  }`}
                >
                  Demander un devis
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accompagnement highlight */}
      <section id="accompagnement" className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div
            className="relative rounded-3xl p-10 md:p-14 overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0A0A0A 0%, #160812 35%, #0B1030 75%, #0A0A0A 100%)",
            }}
          >
            <div className="absolute -top-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#E52431]/25 blur-[100px]" />
            <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-[#1F2C6E]/30 blur-[100px]" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-6">
                <Sparkles size={11} /> L&rsquo;offre phare
              </div>

              <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1]">
                Envie d&rsquo;aller{" "}
                <span className="gradient-text font-medium">plus loin</span> que
                le logo&nbsp;?
              </h2>
              <p className="mt-6 text-white/75 text-lg font-light max-w-2xl">
                L&rsquo;Accompagnement complet EM Group prend en charge chaque
                étape du lancement ou de la montée en puissance de votre
                entreprise à Maurice&nbsp;: branding, visuels, site web,
                stratégie, communication et acquisition. Un seul interlocuteur,
                une équipe senior dédiée — local et international.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
                {[
                  "Branding complet sur-mesure",
                  "Création de site web",
                  "Visuels & stratégie de communication",
                  "Lancement & acquisition",
                ].map((f) => (
                  <div
                    key={f}
                    className="flex items-center gap-3 text-white/85"
                  >
                    <Check
                      size={15}
                      className="text-[#E52431] flex-shrink-0"
                    />
                    <span className="font-light text-sm md:text-base">
                      {f}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/55">
                    À partir de
                  </div>
                  <div className="font-outfit text-5xl font-light text-white mt-1">
                    3490 € TTC
                  </div>
                </div>
                <button
                  onClick={openQuote}
                  data-testid="accompagnement-cta"
                  className="sm:ml-auto inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
                >
                  Être accompagné
                  <ArrowRight size={16} />
                </button>
              </div>

              <p className="mt-6 text-white/55 text-sm font-light">
                <a
                  href="/#accompagnement"
                  data-testid="accompagnement-internal-link"
                  className="text-[#E52431] font-medium hover:underline"
                >
                  En savoir plus sur l&rsquo;accompagnement complet EM Group →
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 section-light">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Ce que vous y gagnez
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Les bénéfices d&rsquo;un{" "}
            <span className="gradient-text font-medium">
              logo professionnel
            </span>
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Award,
                title: "Professionnalisme instantané",
                desc: "Un logo de qualité positionne votre entreprise au niveau des standards internationaux dès le premier regard — crucial pour une clientèle touristique et étrangère.",
              },
              {
                icon: Palette,
                title: "Une image cohérente",
                desc: "Une identité visuelle uniforme sur vos réseaux, votre site, vos supports print et vos documents commerciaux crée instantanément la confiance.",
              },
              {
                icon: TrendingUp,
                title: "Plus de clients, plus vite",
                desc: "Une marque forte attire, rassure et convertit mieux qu'une communication improvisée — particulièrement sur un marché comparatif comme Maurice.",
              },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="light-card light-card-hover rounded-3xl p-8 transition-all duration-500"
                >
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#E52431]/15 to-[#1F2C6E]/15 border border-[#E52431]/20 grid place-items-center mb-5">
                    <Icon size={20} className="text-[#E52431]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-outfit text-xl font-medium text-[#0A0A0A]">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-[#0A0A0A]/65 font-light leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Local SEO - Zones */}
      <section
        data-testid="local-zones"
        className="py-20 md:py-28 bg-[#0A0A0A] border-t border-white/5"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
              — Présence à Maurice
            </span>
            <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
              EM Group accompagne les{" "}
              <span className="gradient-text font-medium">
                entreprises mauriciennes
              </span>
            </h2>
            <div className="mt-6 space-y-4 text-white/70 text-lg font-light leading-relaxed">
              <p>
                Depuis plusieurs années, EM Group accompagne les entreprises
                mauriciennes dans la construction de leur image de marque
                — du premier logo à une identité visuelle pleinement
                déployée. Notre approche de la{" "}
                <strong className="text-white font-normal">
                  création de logo à l&rsquo;île Maurice
                </strong>{" "}
                consiste à allier exigence esthétique, cohérence stratégique
                et compréhension fine des réalités locales, pour que chaque
                marque inspire confiance dès le premier regard.
              </p>
              <p>
                Nous collaborons avec des entrepreneurs et des PME ancrés
                dans toute l&rsquo;île&nbsp;: hôteliers et restaurateurs de{" "}
                <strong className="text-white font-normal">Grand Baie</strong>,
                de <strong className="text-white font-normal">Flic-en-Flac</strong>{" "}
                ou de <strong className="text-white font-normal">Tamarin</strong>,
                commerces et services implantés à{" "}
                <strong className="text-white font-normal">Port-Louis</strong>,{" "}
                <strong className="text-white font-normal">Quatre Bornes</strong>{" "}
                ou <strong className="text-white font-normal">Curepipe</strong>,
                mais aussi start-ups, cabinets de conseil et sociétés
                digitales installés dans les nouveaux pôles d&rsquo;affaires
                de <strong className="text-white font-normal">Ébène</strong>{" "}
                et de <strong className="text-white font-normal">Moka</strong>
                . Ces zones en plein développement concentrent aujourd&rsquo;hui
                une partie significative de la dynamique économique de l&rsquo;île,
                et exigent un niveau de branding à la hauteur de leurs
                ambitions internationales.
              </p>
              <p>
                Que votre activité s&rsquo;adresse à une clientèle locale,
                touristique ou étrangère, nous concevons des identités
                visuelles pensées pour convertir — logos, chartes, univers
                de marque, déclinaisons digitales et print. Et parce que le
                digital abolit les distances, EM Group accompagne également
                ses clients <strong className="text-white font-normal">
                à distance, partout en France et à l&rsquo;international
                </strong>
                , avec un processus fluide en visio, téléphone et WhatsApp.
              </p>
            </div>
          </div>

          {/* Zone chips */}
          <div className="mt-12 flex flex-wrap gap-3">
            {[
              "Port-Louis",
              "Grand Baie",
              "Flic-en-Flac",
              "Ébène",
              "Quatre Bornes",
              "Curepipe",
              "Rose Hill",
              "Trou aux Biches",
              "Pointe aux Canonniers",
              "Tamarin",
              "Black River",
              "Mahébourg",
              "Pamplemousses",
              "Moka",
              "Beau Bassin",
            ].map((zone, i) => (
              <motion.span
                key={zone}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.03 }}
                data-testid={`zone-chip-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 text-sm font-light hover:border-[#E52431]/40 hover:text-white hover:bg-white/10 transition-all duration-300"
              >
                <MapPin size={12} className="text-[#E52431]" />
                {zone}
              </motion.span>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              {
                title: "Tourisme & hôtellerie",
                desc: "Identités visuelles premium pour hôtels, villas, tour-opérateurs et restaurants à Grand Baie, Flic-en-Flac ou Trou aux Biches — pensées pour séduire une clientèle internationale exigeante.",
              },
              {
                title: "Services & conseil",
                desc: "Branding professionnel pour cabinets d'avocats, comptables, sociétés financières et consultants à Ébène, Port-Louis ou Quatre Bornes — pour inspirer confiance dès le premier rendez-vous.",
              },
              {
                title: "Commerce & e-commerce",
                desc: "Logos et chartes pour boutiques locales, e-commerce mauriciens, start-ups tech et créateurs — prêts à s'exporter au-delà de l'île.",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="rounded-2xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 p-6 hover:border-white/20 transition-all duration-500"
              >
                <h3 className="font-outfit text-lg font-medium text-white">
                  {b.title}
                </h3>
                <p className="mt-3 text-white/60 font-light text-sm leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-white/55 text-sm font-light">
            Vous êtes basé à Maurice et votre zone n&rsquo;est pas citée ?
            Nous couvrons l&rsquo;ensemble de l&rsquo;île et travaillons
            également{" "}
            <strong className="text-white font-normal">
              à distance partout en France, à La Réunion et à
              l&rsquo;international
            </strong>{" "}
            —{" "}
            <button
              onClick={openQuote}
              data-testid="local-cta-parlons"
              className="text-[#E52431] font-medium hover:underline"
            >
              parlons de votre projet
            </button>
            .
          </p>
        </div>
      </section>

      {/* Bureau local - Beau Bassin, Maurice */}
      <section className="py-20 md:py-28 section-light" data-testid="bureau-local">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Bureau local
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Un bureau à{" "}
            <span className="gradient-text font-medium">Beau Bassin – Rose Hill</span>,
            au cœur de l&rsquo;île Maurice
          </h2>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 space-y-5 text-[#0A0A0A]/70 text-lg font-light leading-relaxed">
              <p>
                EM Group dispose d&rsquo;un{" "}
                <strong className="text-[#0A0A0A] font-normal">
                  bureau local à Beau Bassin – Rose Hill
                </strong>
                , au cœur de l&rsquo;île Maurice, à quelques minutes des
                pôles d&rsquo;Ébène, de Quatre Bornes et de Port-Louis.
                Cette implantation nous permet de travailler directement
                aux côtés des entreprises mauriciennes, en rendez-vous
                physique ou en visio, avec une équipe qui comprend les
                réalités du marché local.
              </p>
              <p>
                Ce bureau nous sert également de base opérationnelle pour
                la production de visuels, la captation photo, les tournages
                et les activations terrain — un atout lorsque votre
                identité doit s&rsquo;incarner physiquement (hôtels,
                restaurants, boutiques, événements). Depuis Beau Bassin,
                Grand Baie, Flic-en-Flac, Tamarin, Curepipe ou Moka
                restent rapidement accessibles.
              </p>
              <p>
                Concrètement, vous bénéficiez d&rsquo;une vraie présence
                mauricienne, doublée de l&rsquo;exigence d&rsquo;une
                agence digitale à rayonnement international. Une équipe
                disponible sur place, joignable par téléphone et WhatsApp,
                pour avancer rapidement sur vos projets de{" "}
                <strong className="text-[#0A0A0A] font-normal">
                  création de logo à l&rsquo;île Maurice
                </strong>
                , de branding ou de site web.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div
                className="rounded-3xl p-8 md:p-10 border border-[#E52431]/20"
                data-testid="bureau-card"
                style={{
                  background:
                    "linear-gradient(135deg, #FFFFFF 0%, #F7F3F4 50%, #F3F5FB 100%)",
                  boxShadow: "0 30px 80px -20px rgba(10,10,10,0.12)",
                }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E52431]/10 border border-[#E52431]/20">
                  <MapPin size={13} className="text-[#E52431]" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#E52431] font-semibold">
                    Bureau · Maurice
                  </span>
                </div>

                <div className="mt-6 font-outfit text-2xl font-medium text-[#0A0A0A]">
                  Beau Bassin – Rose Hill
                </div>
                <div className="mt-1 text-[#0A0A0A]/60 font-light text-sm">
                  République de Maurice
                </div>

                <div className="mt-7 h-px bg-gradient-to-r from-[#E52431]/30 via-[#1F2C6E]/30 to-transparent" />

                <ul className="mt-7 space-y-3 text-[#0A0A0A]/75 font-light text-sm">
                  {[
                    "Rendez-vous en visio ou sur site",
                    "Production visuels, photo, tournages",
                    "Proximité Ébène, Quatre Bornes, Port-Louis",
                    "Zone de déplacement : toute l'île",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-3">
                      <Check
                        size={14}
                        className="text-[#E52431] mt-1 flex-shrink-0"
                      />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-col gap-3">
                  <a
                    href="tel:+23057438178"
                    data-testid="bureau-phone-call"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full gradient-bg text-white font-semibold py-3.5 text-xs uppercase tracking-[0.15em] hover:shadow-[0_10px_30px_rgba(229,36,49,0.3)] transition-all duration-300 btn-shine"
                  >
                    <Phone size={14} />
                    Appeler maintenant · +230 5743 8178
                  </a>
                  <a
                    href="https://wa.me/23057438178?text=Bonjour%20EM%20Group%2C%20je%20souhaite%20%C3%A9changer%20sur%20un%20projet%20de%20logo%20%C3%A0%20Maurice."
                    target="_blank"
                    rel="noreferrer"
                    data-testid="bureau-whatsapp"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white font-semibold py-3.5 text-xs uppercase tracking-[0.15em] hover:bg-[#25D366] transition-all duration-300"
                  >
                    <MessageCircle size={14} />
                    WhatsApp · +230 5743 8178
                  </a>
                  <button
                    onClick={openQuote}
                    data-testid="bureau-cta"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[#0A0A0A]/15 text-[#0A0A0A] font-semibold py-3 text-xs uppercase tracking-[0.15em] hover:border-[#E52431]/50 hover:text-[#E52431] transition-all duration-300"
                  >
                    Demander un devis
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#0A0A0A]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Questions fréquentes
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
            Création de logo à l&rsquo;île Maurice :{" "}
            <span className="gradient-text font-medium">tout ce qu&rsquo;il faut savoir</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg font-light">
            Les réponses aux questions que se posent les entrepreneurs
            mauriciens avant de lancer leur projet d&rsquo;identité
            visuelle.
          </p>

          <div className="mt-12">
            <Accordion
              type="single"
              collapsible
              defaultValue="faq-0"
              className="space-y-3"
            >
              {FAQ_ITEMS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-2xl px-6 bg-white/[0.04] border border-white/10"
                >
                  <AccordionTrigger className="text-left font-outfit text-lg font-medium text-white hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 font-light leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={openQuote}
              data-testid="faq-final-cta"
              className="inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-10 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
            >
              Démarrer mon projet
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Voir aussi - Internal linking */}
      <section
        data-testid="voir-aussi-section"
        className="py-20 md:py-24 section-light border-t border-black/5"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Pour aller plus loin
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl font-light tracking-tight text-[#0A0A0A]">
            Explorer l&rsquo;univers{" "}
            <span className="gradient-text font-medium">EM Group</span>
          </h2>
          <p className="mt-4 text-[#0A0A0A]/65 font-light max-w-2xl">
            La création de logo n&rsquo;est qu&rsquo;une brique. Découvrez
            l&rsquo;ensemble des services et des pages locales pensés pour
            faire grandir votre entreprise.
          </p>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <a
              href="/#accompagnement"
              data-testid="voir-aussi-accompagnement"
              className="group light-card light-card-hover rounded-3xl p-7 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                Offre phare
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Accompagnement complet
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Branding, site, stratégie et acquisition — pilotés par une
                seule équipe, de A à Z.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                En savoir plus <ArrowRight size={14} />
              </span>
            </a>

            <a
              href="/#branding"
              data-testid="voir-aussi-branding"
              className="group light-card light-card-hover rounded-3xl p-7 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                Branding
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Packs branding &amp; visuels
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Logos, chartes graphiques, packs réseaux sociaux et
                déclinaisons.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir les packs <ArrowRight size={14} />
              </span>
            </a>

            <Link
              to="/creation-logo-la-reunion"
              data-testid="voir-aussi-logo-reunion"
              className="group rounded-3xl p-7 bg-gradient-to-b from-[#E52431]/[0.06] to-transparent border border-[#E52431]/25 hover:border-[#E52431]/50 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                SEO local
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Création de logo à La Réunion
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Vous avez un projet dans le 974 ? Découvrez notre offre
                dédiée aux entreprises réunionnaises.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir la page <ArrowRight size={14} />
              </span>
            </Link>

            <Link
              to="/creation-logo-paris"
              data-testid="voir-aussi-logo-paris"
              className="group rounded-3xl p-7 bg-gradient-to-b from-[#1F2C6E]/[0.08] to-transparent border border-[#1F2C6E]/25 hover:border-[#1F2C6E]/60 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                SEO local
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Création de logo à Paris
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Vous êtes basé à Paris ou en Île-de-France ? Découvrez
                notre offre dédiée aux entreprises parisiennes.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir la page <ArrowRight size={14} />
              </span>
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            <a
              href="/#sites-web"
              data-testid="voir-aussi-pill-sites-web"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              Création de site web
            </a>
            <a
              href="/#tarifs"
              data-testid="voir-aussi-pill-tarifs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              Tarifs
            </a>
            <a
              href="/#audit"
              data-testid="voir-aussi-pill-audit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              Audit 190 € TTC
            </a>
            <a
              href="/#faq"
              data-testid="voir-aussi-pill-faq"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              FAQ générale
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #E52431 0%, #7E1945 35%, #2A2A5F 70%, #1F2C6E 100%)",
        }}
      >
        <div className="absolute inset-0 bg-[#0A0A0A]/20" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            Votre{" "}
            <span className="font-medium italic">nouveau logo</span> commence
            ici.
          </h2>
          <p className="mt-6 text-white/80 text-lg font-light max-w-2xl mx-auto">
            Parlez-nous de votre projet à l&rsquo;île Maurice. Nous
            revenons vers vous sous 24h avec un plan personnalisé et un
            devis précis.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={openQuote}
              data-testid="final-cta-logo-maurice"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0A0A] font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:scale-[1.03] hover:shadow-[0_0_50px_rgba(255,255,255,0.3)] transition-all duration-300 btn-shine"
            >
              Créer mon logo dès maintenant
              <ArrowRight size={16} />
            </button>
            <a
              href="tel:+23057438178"
              data-testid="final-cta-phone"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold px-8 py-5 text-sm uppercase tracking-[0.15em] hover:bg-white/20 transition-all duration-300"
            >
              <Phone size={14} />
              +230 5743 8178
            </a>
            <a
              href="https://wa.me/23057438178?text=Bonjour%20EM%20Group%2C%20je%20souhaite%20%C3%A9changer%20sur%20un%20projet%20de%20logo%20%C3%A0%20Maurice."
              target="_blank"
              rel="noreferrer"
              data-testid="final-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-8 py-5 text-sm uppercase tracking-[0.15em] hover:bg-[#1ebe59] transition-all duration-300"
            >
              <MessageCircle size={14} />
              WhatsApp
            </a>
          </motion.div>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-white/70">
            Maurice · Bureau à Beau Bassin · Réponse sous 24h
          </p>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="py-10 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div>
            © {new Date().getFullYear()} EM Group — Agence digitale premium.
          </div>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-white/80">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-white/80">
              Confidentialité
            </Link>
            <Link to="/cgv" className="hover:text-white/80">
              CGV
            </Link>
          </div>
        </div>
      </footer>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
